import {
  FOLDER_ROOT_PATH,
  FolderPath,
  folderEntryPath,
  folderPathName,
  isDescendantFolderPath,
  parentFolderPath,
} from '@lib/library/folderPath';
import formCss from '@lib/style/form.module.scss';
import { pluralize } from '@lib/utils/units';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { LibraryFolderStore } from '@stores/Library/LibraryFolder';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { FolderDialogShell } from './FolderDialogShell';
import css from './FolderDialogs.module.scss';

/**
 * Confirms deleting a folder. The backend deletes the media inside it along with it, so the
 * dialog says how much is under it, counted across the whole subtree — the number the user needs
 * is everything that disappears, not just what the folder holds directly. When there is media,
 * the user picks between keeping it (moved to the folder's parent, the default: it can't be
 * undone the other way) and deleting it too.
 */
export const FolderDeleteDialog = observer(function FolderDeleteDialog({
  folder,
  path,
  onClose,
  onConfirm,
}: {
  folder: LibraryFolderStore;
  path: FolderPath;
  onClose: () => void;
  onConfirm: (keepMedia: boolean) => Promise<void>;
}) {
  const [keepMedia, setKeepMedia] = useState(true);
  const { mediaCount } = folder.folderSummary(path);
  const subfolderCount = folder.folders.filter((entry) =>
    isDescendantFolderPath(folderEntryPath(entry), path),
  ).length;

  const parent = parentFolderPath(path);
  const parentName =
    parent === FOLDER_ROOT_PATH ? (folder.library.name ?? 'the library') : folderPathName(parent);
  const deletesMedia = mediaCount > 0 && !keepMedia;

  const name = folderPathName(path);
  const folders = subfolderCount > 0 ? pluralize(subfolderCount, 'folder') : null;

  return (
    <FolderDialogShell
      modalName="folder-delete"
      open
      onClose={onClose}
      title="Delete folder?"
      confirmLabel={deletesMedia ? 'Delete folder and media' : 'Delete folder'}
      confirmVariant="danger"
      loadingText="Deleting…"
      onConfirm={() => onConfirm(keepMedia)}
    >
      {mediaCount > 0 ? (
        <>
          <p className={css.body}>
            {folders ? (
              <>
                <strong>{name}</strong> and the <strong>{folders}</strong> inside it hold{' '}
              </>
            ) : (
              <>
                <strong>{name}</strong> holds{' '}
              </>
            )}
            <strong>{pluralize(mediaCount, 'media')}</strong>.
          </p>
          <RadioGroup
            className={formCss.radioGroup}
            value={keepMedia ? 'keep' : 'delete'}
            onChange={(event) => setKeepMedia(event.target.value === 'keep')}
          >
            <FormControlLabel
              value="keep"
              control={<Radio />}
              className={formCss.radioOption}
              label={
                <>
                  <span>Keep the media</span>
                  <span className={formCss.fieldHint}>
                    It moves to “{parentName}”
                    {folders ? `, and the ${folders} inside are deleted` : ''}.
                  </span>
                </>
              }
            />
            <FormControlLabel
              value="delete"
              control={<Radio />}
              className={formCss.radioOption}
              label={
                <>
                  <span>Delete the media too</span>
                  <span className={classNames(formCss.fieldHint, deletesMedia && css.warning)}>
                    It’s deleted permanently. This can’t be undone.
                  </span>
                </>
              }
            />
          </RadioGroup>
        </>
      ) : (
        <p className={css.body}>
          {folders ? (
            <>
              Deleting <strong>{name}</strong> also deletes the <strong>{folders}</strong> inside
              it. They hold no media.
            </>
          ) : (
            <>
              <strong>{name}</strong> is empty.
            </>
          )}
        </p>
      )}
    </FolderDialogShell>
  );
});
